<template>
    <div class="flex flex-wrap gap-4 items-center">
        <div
            v-for="method in visibleMethods"
            :key="method.id || method.code || method.name"
            class="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded font-bold text-gray-600 text-xs shadow-sm"
        >
            <img
                v-if="methodLogo(method)"
                :src="methodLogo(method)"
                :alt="methodLabel(method)"
                class="h-5 w-auto object-contain max-w-[60px]"
            />
            <span>{{ methodLabel(method) }}</span>
        </div>

        <button
            v-if="!showAll && extraCount > 0"
            type="button"
            class="text-xs font-bold text-hu-green hover:underline"
            @click="showAll = true"
        >
            {{ moreLabel }}
        </button>

        <button
            v-if="showAll && extraCount > 0"
            type="button"
            class="text-xs font-bold text-hu-green hover:underline"
            @click="showAll = false"
        >
            {{ lessLabel }}
        </button>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';

defineOptions({ name: 'PaymentMethodsPills' });

const store = useStore();
const showAll = ref(false);

const texts = computed(() => store.getters.texts || {});
const rawMethods = computed(() => store.getters['form/paymentMethodsHowItWorks'] || []);

const orderedMethods = computed(() => {
    const list = rawMethods.value.slice();
    return list.sort((a, b) => {
        const na = (a.translated?.name || a.name || a.code || '').toString().toLowerCase();
        const nb = (b.translated?.name || b.name || b.code || '').toString().toLowerCase();
        return na.localeCompare(nb);
    });
});

const visibleMethods = computed(() => {
    if (showAll.value) return orderedMethods.value;
    return orderedMethods.value.slice(0, 6);
});

const extraCount = computed(() => {
    const total = orderedMethods.value.length;
    return total > 6 ? total - 6 : 0;
});

/** CMS: ":count", "{count}", "%d", or legacy "+ 15 more" — number is replaced with extraCount. */
function formatMoreLessLabel(template, count, fallback) {
    const n = String(count);
    if (!template) return fallback.replace(/:count|\{count\}|%d/gi, n);
    if (/:count|\{count\}|\{\{count\}\}|%d|%s/i.test(template)) {
        return template.replace(/:count|\{count\}|\{\{count\}\}|%d|%s/gi, n);
    }
    if (/\d+/.test(template)) {
        return template.replace(/\d+/, n);
    }
    return `${template} (${n})`;
}

const moreLabel = computed(() =>
    formatMoreLessLabel(
        texts.value['howitworks-section-2.blade.php-2-payment-more'],
        extraCount.value,
        `+ :count more`
    )
);
const lessLabel = computed(
    () => texts.value['howitworks-section-2.blade.php-2-payment-less'] || 'Less'
);

function methodLabel(method) {
    return method?.translated?.name ?? method?.name ?? method?.code ?? '';
}

function methodLogo(method) {
    if (!method) return '';
    const img = method.image ?? method.logo ?? method.thumbnail;
    if (typeof img === 'string') return img;
    return img?.src ?? img?.url ?? '';
}

onMounted(async () => {
    if (rawMethods.value.length) return;
    try {
        await store.dispatch('form/loadPaymentMethodsForHowItWorks');
    } catch (e) {
        // ignore — hide methods if the request failed
    }
});
</script>

<style scoped>
</style>
